class PackageBikeSelector extends HTMLElement {
  constructor() {
    super();

    this.selectedBike = null;
    this.selectedVariantId = null;
    this.selectedVariantPrice = null;
  }

  connectedCallback() {
    this.form = document.getElementById(this.dataset.formId);
    this.cartType = this.dataset.cartType;
    this.productTitle = this.dataset.productTitle || '';

    this.bikeButtons = Array.from(this.querySelectorAll('[data-bike-button]'));
    this.bikeList = this.querySelector('.package-bike-selector__bikes');

    // The package product's own media gallery, which picking a bike moves.
    this.gallery =
      document.getElementById(`${this.dataset.sectionId}-slider`) ||
      document.querySelector('.product-single__media .js-product-slider');
    this.colorGroups = Array.from(this.querySelectorAll('[data-colors-for]'));
    this.colorsStep = this.querySelector('[data-colors-step]');
    this.promptBike = this.querySelector('[data-prompt-bike]');
    this.addStep = this.querySelector('[data-add-step]');
    this.addButton = this.querySelector('[data-add-button]');
    this.addButtonContainer = this.querySelector('.js-product-buttons');
    this.addButtonText = this.querySelector('[data-add-button-text]');
    this.addButtonLabel = this.addButtonText
      ? this.addButtonText.textContent.trim()
      : '';
    this.summary = this.querySelector('[data-summary]');
    this.errorContainer = this.querySelector('[data-error]');

    this.total = this.querySelector('[data-total]');
    this.totalBike = this.querySelector('[data-total-bike]');
    this.totalPackage = this.querySelector('[data-total-package]');
    this.totalPackageRow = this.querySelector('[data-total-package-row]');

    const packageVariantsJson = this.querySelector('[data-package-variants]');
    this.packageVariants = packageVariantsJson
      ? JSON.parse(packageVariantsJson.textContent)
      : {};

    this._boundBikeClick = this.onBikeClick.bind(this);
    this._boundColorChange = this.onColorChange.bind(this);
    this._boundAdd = this.onAdd.bind(this);
    this._boundPackageVariantChange = this.onPackageVariantChange.bind(this);

    // The variant the package form is currently rendered with. Kept in step
    // with the variant picker below; null once the picker lands on a
    // combination of options that has no variant at all.
    this.packageVariantId =
      this.form?.elements['id']?.value || this.dataset.defaultVariantId;

    this.addEventListener('click', this._boundBikeClick);
    this.addEventListener('change', this._boundColorChange);
    if (this.addButton) this.addButton.addEventListener('click', this._boundAdd);

    // The package's own variant picker; this block owns the only buy button on
    // the page, so it has to follow the picker itself.
    this.variantPicker = document.querySelector(
      `variant-selects[data-section="${this.dataset.sectionId}"], variant-radios[data-section="${this.dataset.sectionId}"]`,
    );
    if (this.variantPicker) {
      this.variantPicker.addEventListener(
        'variants:change',
        this._boundPackageVariantChange,
      );
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._boundBikeClick);
    this.removeEventListener('change', this._boundColorChange);
    if (this.addButton)
      this.addButton.removeEventListener('click', this._boundAdd);
    if (this.variantPicker) {
      this.variantPicker.removeEventListener(
        'variants:change',
        this._boundPackageVariantChange,
      );
    }
  }

  onPackageVariantChange(event) {
    this.packageVariantId = event.detail.variant
      ? String(event.detail.variant.id)
      : null;

    this.updateAddStep();
  }

  onBikeClick(event) {
    const button = event.target.closest('[data-bike-button]');
    if (!button || !this.contains(button)) return;

    event.preventDefault();
    this.selectBike(button);
  }

  selectBike(button) {
    this.removeError();

    this.selectedBike = {
      id: button.dataset.bikeId,
      title: button.dataset.bikeTitle,
    };
    this.selectedVariantId = null;
    this.selectedVariantPrice = null;

    this.bikeButtons.forEach((bikeButton) => {
      bikeButton.classList.toggle('is-selected', bikeButton === button);
      bikeButton.setAttribute('aria-pressed', bikeButton === button);
    });
    if (this.bikeList) this.bikeList.classList.add('has-selection');
    if (this.promptBike) this.promptBike.textContent = this.selectedBike.title;

    this.showBikeMedia(button.dataset.bikeIndex);

    const group = this.colorGroups.find(
      (colorGroup) => colorGroup.dataset.colorsFor === this.selectedBike.id,
    );

    this.colorGroups.forEach((colorGroup) => {
      colorGroup.hidden = colorGroup !== group;
      // Reset any previously made colour choice on the groups we hide
      if (colorGroup !== group) {
        colorGroup
          .querySelectorAll('input[type="radio"]:checked')
          .forEach((input) => {
            input.checked = false;
          });
      }
    });

    // Bikes without a colour option go straight to the add to cart step
    if (group && group.dataset.singleVariantId) {
      this.selectedVariantId = group.dataset.singleVariantId;
      this.selectedVariantPrice = Number(group.dataset.singleVariantPrice);
    }

    if (this.colorsStep) {
      this.colorsStep.hidden = !group || Boolean(group.dataset.singleVariantId);
    }

    this.updateAddStep();
  }

  /**
   * Moves the package gallery to the image that goes with the chosen bike: the
   * package's media is ordered to match the eligible bikes, so the nth bike is
   * shown by the nth image. A package with fewer images than bikes leaves the
   * gallery where it is rather than jumping to something unrelated.
   */
  showBikeMedia(bikeIndex) {
    const index = Number(bikeIndex);
    if (!this.gallery || !Number.isInteger(index) || index < 0) return;

    const slide = this.gallery.querySelector(
      `.media-gallery__item[data-slide-id="${index}"]`,
    );
    if (!slide) return;

    // Slick owns the gallery once it has initialised, which happens the first
    // time it scrolls into view. Before that, the attribute it reads for its
    // starting slide is the only way in.
    if (
      typeof $ === 'function' &&
      this.gallery.classList.contains('slick-initialized')
    ) {
      $(this.gallery).slick('slickGoTo', index);
    } else {
      this.gallery.setAttribute('data-slide-id', String(index));
    }
  }

  onColorChange(event) {
    const input = event.target.closest('[data-color-input]');
    if (!input || !this.contains(input)) return;

    this.removeError();

    this.selectedVariantId = input.dataset.variantId;
    this.selectedVariantPrice = Number(input.dataset.variantPrice);

    this.updateAddStep();
  }

  updateAddStep() {
    const ready = Boolean(this.selectedBike && this.selectedVariantId);

    if (this.addStep) this.addStep.hidden = !ready;
    if (!ready) return;

    const packageVariant = this.packageVariantId
      ? this.packageVariants[this.packageVariantId]
      : null;

    this.renderTotal();

    const packageAvailable = packageVariant
      ? packageVariant.available !== false
      : false;

    if (this.addButton) {
      this.addButton.disabled = !packageAvailable;
    }
    if (this.addButtonContainer) {
      this.addButtonContainer.classList.toggle('is-disabled', !packageAvailable);
    }
    if (this.addButtonText) {
      if (packageAvailable) {
        this.addButtonText.textContent = this.addButtonLabel;
      } else {
        this.addButtonText.textContent = this.packageVariantId
          ? this.dataset.soldOutText
          : this.dataset.errorText;
      }
    }
  }

  /**
   * Breaks the purchase down as bike and package, then the final price.
   *
   * The package is a product of its own, so its figure comes off the variant
   * the picker is on: the price it sells for, which is what the customer
   * actually pays for the bundle rather than what it is worth undiscounted.
   * The new total is the bike plus that price, which is the sum of the rows
   * above it.
   *
   * Hidden rather than approximated if the bike's price is missing, or if the
   * money formatter has not loaded.
   */
  renderTotal() {
    if (!this.summary) return;

    const formatMoney = window.Shopify && window.Shopify.formatMoney;
    const bikePrice = this.selectedVariantPrice;

    if (!formatMoney || !Number.isFinite(bikePrice)) {
      this.summary.hidden = true;
      return;
    }

    const format =
      window.theme.money_total_price_format || window.theme.money_format;
    const money = (cents) => formatMoney(cents, format);

    const packageVariant = this.packageVariantId
      ? this.packageVariants[this.packageVariantId]
      : null;
    const packagePrice = Number(packageVariant?.price) || 0;

    if (this.totalBike) this.totalBike.textContent = money(bikePrice);
    if (this.totalPackageRow) this.totalPackageRow.hidden = packagePrice === 0;
    if (this.totalPackage) this.totalPackage.textContent = money(packagePrice);
    if (this.total) {
      this.total.textContent = money(bikePrice + packagePrice);
    }

    this.summary.hidden = false;
  }

  /**
   * Any line item properties collected on the package form. They belong to the
   * package, which is the line the customer filled them in for.
   *
   * Property fields can sit outside the form and be associated with it only
   * through their `form` attribute, so this reads `form.elements` rather than
   * querying the form's descendants.
   */
  getFormProperties() {
    const properties = {};
    if (!this.form) return properties;

    Array.from(this.form.elements).forEach((field) => {
      if (!field.name || !field.name.startsWith('properties[')) return;
      if (
        (field.type === 'checkbox' || field.type === 'radio') &&
        !field.checked
      )
        return;
      if (field.value === '') return;

      properties[field.name] = field.value;
    });

    return properties;
  }

  /**
   * The package itself, which carries the price of the deal and whatever the
   * customer filled in on its form. A package is always bought one at a time,
   * so the quantity is fixed at 1.
   *
   * `_requires_bike` is what the cart checks the package against: the price is
   * only the deal price while a bike is bought with it, so a package left in
   * the cart on its own is taken back out.
   */
  getPackageItem() {
    return {
      ...this.getFormProperties(),
      id: this.packageVariantId,
      quantity: 1,
      'properties[_requires_bike]': 'true',
      'properties[_bundle]': 'Package Deal',
    };
  }

  /**
   * The bike the customer chose, at the colour they chose. `Type` is what marks
   * it as a bike a package can be counted against, and `Package` names the deal
   * it was bought with — which is both what staff read off the line and what
   * ties this bike to that package, so removing this bike takes that package
   * out of the cart and leaves any other package in there untouched.
   */
  getBikeItem() {
    const item = {
      id: this.selectedVariantId,
      quantity: 1,
      'properties[Type]': 'Main Bike',
      'properties[_bundle]': 'Package Deal',
    };

    if (this.productTitle) item['properties[Package]'] = this.productTitle;

    return item;
  }

  async onAdd(event) {
    event.preventDefault();

    if (!this.selectedBike || !this.selectedVariantId || !this.packageVariantId)
      return;

    try {
      this.removeError();
      await this._setIsAdding();
      // The package carries the price of the bundle, so the cart takes it and
      // the chosen bike and nothing else: the accessories it comes with are
      // part of what the package costs, not lines of their own.
      await window.theme.cart.store
        .getState()
        .add([this.getPackageItem(), this.getBikeItem()]);
      await this._setIsAdding(false);

      if (this.cartType === 'page') {
        window.location.href = `${window.Shopify.routes.root}cart`;
        return;
      }

      await window.theme.cart.store.getState().resetVariantsBeingAdded();
      if (!this.closest('.cart-draw')) window.theme.mfpOpen('cart');
    } catch (e) {
      this._renderError(e.message);
      await this._setIsAdding(false, true);
    }
  }

  _setIsAdding(isAdding = true, error = false) {
    return new Promise((resolve) => {
      if (!this.addButton) {
        resolve('No submit button');
        return;
      }

      if (error) {
        this.addButton.classList.remove('is-adding');
        resolve('error');
        return;
      }

      if (isAdding) {
        this.addButton.classList.remove('is-added');
        this.addButton.classList.add('is-adding');
        resolve('Adding');
      } else {
        this.addButton.classList.remove('is-adding');
        this.addButton.classList.add('is-added');

        this.addButton.addEventListener(
          'animationend',
          () => {
            this.addButton.classList.remove('is-added');
            resolve('Added');
          },
          { once: true },
        );
      }
    });
  }

  _renderError(message) {
    if (!this.errorContainer) return;

    this.errorContainer.innerHTML = message;
    this.errorContainer.hidden = false;
  }

  removeError() {
    if (!this.errorContainer) return;

    this.errorContainer.innerHTML = '';
    this.errorContainer.hidden = true;
  }
}

if (!customElements.get('package-bike-selector')) {
  customElements.define('package-bike-selector', PackageBikeSelector);
}
